import {Box, Modal, ModalProps} from '@mui/material';
import uniqueId from 'lodash/uniqueId';
import {Resizable} from 're-resizable';
import {ReactNode, useEffect, useRef, useState} from 'react';
import Draggable from 'react-draggable';

import {useAppDispatch, useAppSelector} from '../../hooks/redux';
import reduxActions from '../../redux/actions';

interface Props extends Omit<ModalProps, 'children'> {
  children?:
    | ModalProps['children']
    | ((params: {isActive: boolean}) => ReactNode | ReactNode[]);
}

const ModalDraggable = ({children, sx, ...props}: Props) => {
  const [modalId] = useState<number>(() => +uniqueId());
  const activeDraggableModal = useAppSelector(
    ({app}) => app.activeDraggableModal
  );

  const reduxDispatch = useAppDispatch();
  const nodeRef = useRef(null);

  const setActive = () => {
    reduxDispatch((state) =>
      reduxActions.app.setApp(state, {
        activeDraggableModal: modalId,
      })
    );
  };

  useEffect(() => {
    setActive();
  }, []);

  const isActive = modalId === activeDraggableModal;

  return (
    <Modal
      hideBackdrop
      disableScrollLock
      disableEnforceFocus
      disableAutoFocus
      closeAfterTransition={true}
      {...props}
      sx={{zIndex: isActive ? 1202 : 1201, pointerEvents: 'none'}}
    >
      {/* Outer DOM node FocusTrap can attach its ref to (fixes rootRef.current.contains) */}
      <Box sx={{outline: 'none'}}>
        <Draggable nodeRef={nodeRef} handle=".dragHandle">
          <Box
            ref={nodeRef}
            sx={{
              position: 'absolute',
              pointerEvents: 'auto',
              top: '30px',
              left: 'calc(50% - 350px)',
              right: 'auto',
              bottom: 'auto',
              transform: 'translate(-50%, -50%)',
              width: 'fit-content',
              bgcolor: 'background.paper',
              border: '1px solid #9b9b9b',
              boxShadow: 24,
              ...sx,
            }}
          >
            <Resizable
              defaultSize={{width: 700, height: 'auto'}}
              minWidth={320}
              minHeight={120}
              maxHeight="85vh"
              enable={{right: true, bottomRight: true}}
              handleStyles={{bottomRight: {zIndex: 1}}}
            >
              <Box
                onMouseDown={setActive}
                sx={{height: '100%', maxHeight: '85vh', overflow: 'auto'}}
              >
                {typeof children === 'function'
                  ? children({isActive})
                  : children}
                <Box
                  className="dragHandle"
                  position="absolute"
                  width="100%"
                  bottom={0}
                  left={0}
                  height={10}
                  sx={{cursor: 'grab'}}
                />
              </Box>
            </Resizable>
          </Box>
        </Draggable>
      </Box>
    </Modal>
  );
};

export default ModalDraggable;
